/** NotaVera N7 (PRD §10 NV-VER): types + pure helpers of the public verification page (tested in nv-verify.test.ts). */
export type Verdict = 'AUTHENTIC' | 'RECORD_FOUND' | 'ALTERED' | 'UNKNOWN';

export interface EnpPublic {
  name: string;
  rollNo: string;
  commissionNo: string | null;
  statusAtSigning: string | null;
  statusToday: string;
  mayActToday: boolean;
  signingCertRevoked: boolean | null;
  signingCertRevokedAt: string | null;
}
export interface SealPublic { version: number; status: 'ISSUED' | 'SUBMITTED' | 'RETIRED'; code: string; enaSubmittedOn: string | null; usable: boolean; retiredAt: string | null; issuedAt: string }
export interface SignatureCheck { present: boolean; intact: boolean; trusted: boolean; timestamp: boolean; signer: string | null; serial: string | null; signingTime: string | null; level: string | null; error: string | null }
export interface DocumentPublic { certificateNo: string | null; verifyCode: string | null; actType: string | null; actTitle: string | null; mode: 'IEN' | 'REN' | null; sealedAt: string | null; bookReference: string | null; sha256: string | null; pages: number | null; pades: string | null; pdfa: string | null }

export type VerifyResult =
  | { found: false; kind?: 'UPLOAD'; verdict: Verdict; reason: string; demo?: boolean; sha256?: string; enp?: EnpPublic | null; upload?: { hashMatch: boolean; signature: SignatureCheck } }
  | { found: true; kind: 'SEAL'; verdict: null; reason: string; demo: boolean; enp: EnpPublic; seal: SealPublic; sealedDocuments: number }
  | { found: true; kind: 'DOCUMENT' | 'CTC'; verdict: Verdict; reason: string; demo: boolean; document: DocumentPublic; enp: EnpPublic; seal: SealPublic | null; signature: { level: string | null; timestampAuthority: string | null; timestamp: string | null; certificateSerial: string | null; validAtSealing: boolean; ltv: boolean } | null; book: { recorded: boolean; chainOk: boolean | null }; upload: { hashMatch: boolean; signature: SignatureCheck | null } | null };

/** Is this a NotaVera code (seal, document, or seal/certificate) — the dispatcher between the NotaVera page and QXM's /verify/:token. */
export const NV_CODE_RE = /^NVD?-[A-Z0-9]{10}(\/\d{4}-\d{4})?$/i;
export const isNotaVeraCode = (token: string | undefined): boolean => !!token && NV_CODE_RE.test(token.trim());

/** Normalise what the visitor typed: upper-case, spaces removed, a pasted URL reduced to its code. */
export function cleanCode(raw: string): string {
  const t = raw.trim().replace(/\s+/g, '');
  const m = t.match(/\/verify\/([^/?#]+(?:\/\d{4}-\d{4})?)/i);
  return (m ? m[1] : t).toUpperCase();
}

export const VERDICT: Record<Verdict, { label: string; tone: 'ok' | 'danger' | 'warn'; heading: string }> = {
  AUTHENTIC: { label: 'Authentic', tone: 'ok', heading: 'Authentic — a notarized document of this ENP' },
  // R review: a code alone proves a record exists, not that the paper or file in hand is genuine
  RECORD_FOUND: { label: 'Record found', tone: 'ok', heading: 'Record found — upload your copy to check it is genuine' },
  ALTERED: { label: 'Altered', tone: 'danger', heading: 'Altered — this copy does not match the sealed output' },
  UNKNOWN: { label: 'Unknown', tone: 'warn', heading: 'Unknown — no record of this document' },
};

/** The one-line status of an ENP's commission for the result card. */
export function commissionLine(e: EnpPublic): { text: string; tone: 'ok' | 'danger' | 'warn' } {
  if (e.mayActToday) return { text: `Commission ${e.statusToday} today`, tone: 'ok' };
  return { text: `Commission ${e.statusToday} today — the ENP may not act`, tone: e.statusToday === 'expired' || e.statusToday === 'pending verification' ? 'warn' : 'danger' };
}

export const fmtWhen = (v?: string | null) => (v ? new Date(v).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Manila' }) : '—');
